import React, { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, VolumeX, Waves } from "lucide-react";
import { useExperience } from "../context/ExperienceContext";
import { playDrum, unlockAudio, startAmbient, stopAmbient } from "../lib/audio";
import { PADS } from "../lib/drums";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// tempo -> emotional colour (therapeutic tones)
const STATE_COLOR = { grounding: "#5b7a99", rhythm: "#b89556", energy: "#c46a58", idle: "#548a88" };

const MESSAGES = {
  grounding: [
    "Grounding. Let each beat settle you.",
    "Slow and steady — you're safe here.",
    "Breathe in… and let the rhythm carry you.",
    "There's no rush. Just this beat, and the next.",
  ],
  rhythm: [
    "You've found your rhythm. Stay with it.",
    "That's your pulse — gentle and steady.",
    "Right here. Let the flow hold you.",
    "You're in it now. Softly, softly.",
  ],
  energy: [
    "Feel the energy rise… and let it soften.",
    "Notice it move through you, then ease.",
    "Strong and steady — let your shoulders drop.",
    "Let the pace settle back into your breath.",
  ],
};

// pattern milestones (consecutive beats within a phrase)
const MILESTONES = {
  4: "Four gentle beats — a rhythm is forming.",
  8: "Eight beats in. You're settling in.",
  16: "Sixteen, and flowing — this is your calm.",
};

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

export default function DrumPad() {
  const { pulse } = useExperience();
  const [ripples, setRipples] = useState([]);
  const [message, setMessage] = useState(null);
  const [tempoState, setTempoState] = useState("idle");
  const [beats, setBeats] = useState(0);
  const [voiceOn, setVoiceOn] = useState(true);
  const [ambientOn, setAmbientOn] = useState(true);

  const tapsRef = useRef([]);
  const beatRef = useRef(0);
  const stateRef = useRef("idle");
  const lastSpeakRef = useRef(0);
  const audioRef = useRef(null);
  const voiceOnRef = useRef(true);
  voiceOnRef.current = voiceOn;
  const ambientOnRef = useRef(true);
  ambientOnRef.current = ambientOn;

  // stop the drone when leaving the page
  useEffect(() => () => stopAmbient(), []);

  const toggleAmbient = useCallback(() => {
    setAmbientOn((v) => {
      const next = !v;
      if (next) startAmbient();
      else stopAmbient();
      return next;
    });
  }, []);

  const speak = useCallback(async (text) => {
    if (!voiceOnRef.current) return;
    const now = Date.now();
    if (now - lastSpeakRef.current < 5200) return; // throttle so it never nags
    lastSpeakRef.current = now;
    try {
      const { data } = await axios.post(`${API}/tts`, { text });
      if (audioRef.current) audioRef.current.pause();
      const a = new Audio(`data:audio/mp3;base64,${data.audio}`);
      a.volume = 0.8;
      audioRef.current = a;
      a.play().catch(() => {});
    } catch (e) {
      /* voice is a bonus — stay silent on failure */
    }
  }, []);

  const hit = useCallback(
    (pad, e) => {
      unlockAudio();
      if (ambientOnRef.current) startAmbient();
      const now = Date.now();
      const taps = tapsRef.current.filter((t) => now - t < 3000);
      const gap = taps.length ? now - taps[taps.length - 1] : Infinity;
      taps.push(now);
      tapsRef.current = taps.slice(-8);

      // consecutive-beat counter (resets after a pause)
      beatRef.current = gap > 2000 ? 1 : beatRef.current + 1;
      setBeats(beatRef.current);

      // tempo state from recent gaps
      const gaps = [];
      for (let i = 1; i < taps.length; i++) gaps.push(taps[i] - taps[i - 1]);
      const recent = gaps.slice(-5);
      const avg = recent.length ? recent.reduce((a, b) => a + b, 0) / recent.length : 900;
      let st = "grounding";
      if (taps.length >= 2) {
        if (avg < 360) st = "energy";
        else if (avg < 750) st = "rhythm";
        else st = "grounding";
      }

      // colored ripple in the drum's own hue
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX ?? rect.left + rect.width / 2;
      const y = e.clientY ?? rect.top + rect.height / 2;
      // musical voice variation: pitch rises subtly as the phrase builds
      const pitch = 1 + Math.min(beatRef.current, 10) * 0.011;
      const velocity = st === "energy" ? 1.1 : st === "rhythm" ? 1.0 : 0.85;
      // spatial audio: pan by the pad's horizontal position on screen (-1 left … +1 right)
      const pan = Math.max(-1, Math.min(1, (x / window.innerWidth) * 2 - 1)) * 0.8;
      playDrum(pad.id, { pitch, velocity, pan });
      pulse(st === "energy" ? 1.7 : st === "rhythm" ? 1.25 : 0.9);
      const rid = now + Math.random();
      setRipples((r) => [...r, { id: rid, x, y, rgb: pad.rgb }]);
      setTimeout(() => setRipples((r) => r.filter((rp) => rp.id !== rid)), 1200);

      // encouragement: milestone first, else on tempo-state change
      const milestone = MILESTONES[beatRef.current];
      let text = null;
      if (milestone) text = milestone;
      else if (st !== stateRef.current) text = pick(MESSAGES[st]);
      if (text) {
        setMessage({ text, color: STATE_COLOR[st] });
        speak(text);
      }
      stateRef.current = st;
      setTempoState(st);
    },
    [pulse, speak]
  );

  const stateColor = STATE_COLOR[tempoState] || STATE_COLOR.idle;

  return (
    <div className="w-full">
      <div className="flex items-center justify-between max-w-xl mb-4">
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-primary-c" data-testid="drum-cta">
          Tap a drum — feel the tension start to ease <span className="inline-block animate-bounce">↓</span>
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleAmbient}
            data-testid="drum-ambient-toggle"
            aria-label={ambientOn ? "Turn off ambient sound" : "Turn on ambient sound"}
            title={ambientOn ? "Ambient drone on" : "Ambient drone off"}
            className={`grid place-items-center h-8 w-8 rounded-full border transition-colors ${ambientOn ? "border-primary-c/60 text-primary-c" : "border-white/15 text-fg/40 hover:text-fg/70"}`}
          >
            <Waves size={15} />
          </button>
          <button
            onClick={() => setVoiceOn((v) => !v)}
            data-testid="drum-voice-toggle"
            aria-label={voiceOn ? "Mute encouragement voice" : "Unmute encouragement voice"}
            className="grid place-items-center h-8 w-8 rounded-full border border-white/15 text-fg/60 hover:text-primary-c hover:border-primary-c/60 transition-colors"
          >
            {voiceOn ? <Volume2 size={15} /> : <VolumeX size={15} />}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 max-w-3xl">
        {PADS.map((pad) => (
          <motion.button
            key={pad.id}
            data-testid={`drum-pad-${pad.id}`}
            onPointerDown={(e) => hit(pad, e)}
            whileTap={{ scale: 0.93 }}
            whileHover={{ y: -3 }}
            className="shape-r relative overflow-hidden py-6 px-4 font-mono text-xs tracking-[0.2em] uppercase glass text-fg/90 transition-colors"
            style={{ border: `1px solid ${pad.color}55`, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)" }}
          >
            <span className="relative z-10 flex items-center justify-center gap-2">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: pad.color, boxShadow: `0 0 10px ${pad.color}` }} />
              {pad.label}
            </span>
            <span
              className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-300"
              style={{ background: `radial-gradient(circle at center, rgba(${pad.rgb},0.28), transparent 70%)` }}
            />
          </motion.button>
        ))}
      </div>

      {/* encouragement + beat meter */}
      <div className="mt-5 max-w-xl min-h-[52px] flex items-center gap-4" data-testid="drum-feedback">
        <div className="flex items-center gap-1.5" data-testid="beat-meter">
          {Array.from({ length: 8 }).map((_, i) => (
            <span
              key={i}
              className="h-2.5 w-2.5 rounded-full transition-all duration-200"
              style={{
                backgroundColor: i < beats ? stateColor : "rgba(255,255,255,0.12)",
                boxShadow: i < beats ? `0 0 10px ${stateColor}` : "none",
                transform: i < beats ? "scale(1.05)" : "scale(1)",
              }}
            />
          ))}
        </div>
        <AnimatePresence mode="wait">
          {message && (
            <motion.p
              key={message.text}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
              className="font-serif italic text-base md:text-lg"
              style={{ color: message.color }}
              data-testid="drum-encouragement"
            >
              {message.text}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      {/* fixed colored ripple overlay */}
      <div className="fixed inset-0 pointer-events-none z-50">
        {ripples.map((r) => (
          <span
            key={r.id}
            className="ripple"
            style={{
              left: r.x,
              top: r.y,
              width: "140vmax",
              height: "140vmax",
              borderColor: `rgba(${r.rgb},0.5)`,
              boxShadow: `0 0 80px 14px rgba(${r.rgb},0.18)`,
            }}
          />
        ))}
      </div>
    </div>
  );
}
